import '@wangeditor/editor/dist/css/style.css';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import { Modal } from 'antd'; // 用于缓存恢复确认弹窗
import { CommonProps } from '../../../utilsTs/declareTypes';
import antdMessage from '../system/antdMessage';
import FilesSelector, { FilesSelectorProps } from '../files/FilesSelector';
import { IMenuGroup } from '@wangeditor/core/dist/core/src/menus/interface';

/** WangeditorV5 组件配置选项（仅受控模式） */
declare interface WangeditorOptions extends Partial<Pick<CommonProps, 'defaultService'>> {
  /** id */
  id?: string;
  /** 内容 */
  value?: string;
  /** 标题 */
  title?: string;
  /** 更改 */
  onChange?: (html: string) => void;
  /** 模式 */
  displayMode?: 'editor' | 'preview';
  /** 预览样式 */
  previewStyle?: React.CSSProperties;
  /**　样式 */
  style?: React.CSSProperties;
  /** 配置 */
  editorConfig?: Partial<IEditorConfig>;
}

/**
 * WangeditorV5 富文本编辑器主组件
 */
const WangeditorV5 = (options: WangeditorOptions) => {
  const {
    id: rawId = 'Default',
    value,
    title,
    onChange,
    displayMode = 'editor',
    previewStyle,
    style,
    editorConfig: customEditorConfig,
  } = options;

  const id = `wangeditorV5_${rawId}`;

  /** 自定义菜单组 */
  class TitleCustomMenuGroup implements IMenuGroup {
    /** 主题 */
    title: string;
    /** 键 */
    key: string;
    /** 菜单集合 */
    menuKeys: Array<string>;
    constructor() {
      this.title = `【${title}】`;
      this.key = 'titleCustomMenuGroup';
      this.menuKeys = [];
    }
  };

  /** 工具栏配置（useMemo 稳定引用，避免每次渲染 new TitleCustomMenuGroup 造成无意义重建） */
  const toolbarConfig: Partial<IToolbarConfig> = useMemo(() => ({
    insertKeys: title ? {
      index: 0, // 插入的位置，基于当前的 toolbarKeys
      keys: [new TitleCustomMenuGroup],
    } : undefined,
  }), [title]);

  const showEditor = displayMode === 'editor';
  // ========== 新增：用 ref 保存最新的 showEditor，避免闭包陷阱 ==========
  const showEditorRef = useRef(showEditor);
  useEffect(() => {
    showEditorRef.current = showEditor;
  }, [showEditor]);

  const editorRef = useRef<IDomEditor | null>(null);
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const unmountedRef = useRef(false);

  // 编辑器实例创建与销毁
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      try {
        if (editorRef.current && !editorRef.current.isDestroyed) {
          editorRef.current.destroy();
        }
      } catch (e) {
        // wangEditor 在 DOM 已被移除时 destroy 可能抛异常，忽略即可
      }
      editorRef.current = null;
    };
  }, []);

  // 正常退出时清除缓存（包括组件卸载、页面关闭/刷新）
  // 只有异常退出（崩溃、断电等）时缓存才会保留，用于下次恢复
  // 仅 editor 模式下生效，预览模式不操作缓存
  useEffect(() => {
    if (!showEditorRef.current) return;

    const clearCache = () => {
      localStorage.removeItem(id);
    };
    window.addEventListener('beforeunload', clearCache);
    return () => {
      clearCache();
      window.removeEventListener('beforeunload', clearCache);
    };
  }, [id, showEditor]); // 依赖 showEditor，模式切换时重新绑定/解绑

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // ========== 缓存恢复逻辑 ==========
  const hasCheckedCache = useRef(false);
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // id 变化时重置缓存检查标记，避免组件被复用到不同 id 时不再检查缓存
  useEffect(() => {
    hasCheckedCache.current = false;
  }, [id]);

  useEffect(() => {
    // 非 editor 模式直接跳过缓存检查
    if (!showEditorRef.current) return;
    if (hasCheckedCache.current) return;
    hasCheckedCache.current = true;
    const cached = localStorage.getItem(id);
    if (cached && cached !== valueRef.current) {
      Modal.confirm({
        title: '恢复未保存的内容',
        content: '检测到上次编辑可能未正常保存，是否恢复之前的内容？',
        okText: '恢复',
        cancelText: '放弃',
        onOk: () => {
          onChangeRef.current?.(cached);
          localStorage.removeItem(id);
        },
        onCancel: () => {
          localStorage.removeItem(id);
        },
      });
    }
  }, [id]);

  const baseEditorConfig: Partial<IEditorConfig> = useMemo(() => ({
    placeholder: '请输入内容...',
    MENU_CONF: {},
    onChange(editor: IDomEditor) {
      const html = editor.getHtml();
      // 仅在 editor 模式下写入缓存
      if (showEditorRef.current) {
        localStorage.setItem(id, html);
      }
      onChangeRef.current?.(html);
    }
  }), [id]);

  const editorConfig: Partial<IEditorConfig> = useMemo(() => ({
    ...baseEditorConfig,
    ...customEditorConfig,
    MENU_CONF: {
      ...baseEditorConfig.MENU_CONF,
      ...(customEditorConfig?.MENU_CONF || {}),
    },
  }), [baseEditorConfig, customEditorConfig]);

  const mergedPreviewStyle: React.CSSProperties = {
    marginTop: showEditor ? 15 : 0,
    ...previewStyle,
  };

  const { height: customHeight, ...restStyle } = style || {};
  let editorHeight = '500px';
  if (customHeight) {
    editorHeight = typeof customHeight === 'number' ? `${customHeight}px` : customHeight;
  }

  const handleEditorCreated = useCallback((ed: IDomEditor) => {
    editorRef.current = ed;
    // 组件已卸载时不再 setState，避免在卸载后更新 state 造成的报错与潜在内存泄漏
    if (unmountedRef.current) return;
    setEditor(ed);
  }, []);

  const editorArea = showEditor ? (
    <div style={{ border: '1px solid #ccc', zIndex: 100 }}>
      <Toolbar editor={editor} defaultConfig={toolbarConfig} mode="default"
        style={{ borderBottom: '1px solid #ccc' }} />
      <Editor
        defaultConfig={editorConfig}
        value={value ?? ''}
        onCreated={handleEditorCreated}
        mode="default"
        style={{ height: editorHeight, overflowY: 'hidden' }}
      />
    </div>
  ) : null;

  const previewArea = !showEditor ? (
    <div style={mergedPreviewStyle} dangerouslySetInnerHTML={{ __html: value || '' }} />
  ) : null;

  return (
    <div style={restStyle}>
      {editorArea}
      {previewArea}
    </div>
  );
};

// ==================== Ant Design 表单包装组件 ====================

declare interface WangeditorV5ForAntDProps extends Omit<WangeditorOptions, 'value' | 'onChange'> {
  value?: string;
  onChange?: (html: string) => void;
}

/**
 * Ant Design Form 兼容的富文本编辑器组件
 * 集成文件选择器，支持自定义插入图片与视频
 */
const WangeditorV5ForAntD: React.FC<WangeditorV5ForAntDProps> = ({
  value,
  onChange,
  ...restProps
}) => {

  const { defaultService, ...editorRestProps } = restProps;

  const handleChange = useCallback(
    (html: string) => onChange?.(html),
    [onChange]
  );

  // ========== 文件选择器相关状态与 ref ==========
  const [filesSelectorVisible, setFilesSelectorVisible] = useState<boolean>(false);

  // 保存图片插入函数的引用
  const insertImageFnRef = useRef<any>(null);
  // 保存视频插入函数的引用
  const insertVideoFnRef = useRef<any>(null);
  // 标记当前打开文件选择器时将要插入的类型
  const [currentInsertType, setCurrentInsertType] = useState<'image' | 'video'>('image');

  // 保存最新的 defaultService，避免闭包陷阱
  const defaultServiceRef = useRef(defaultService);
  useEffect(() => {
    defaultServiceRef.current = defaultService;
  }, [defaultService]);

  // 组件卸载时清理 insertFn 引用
  useEffect(() => {
    return () => {
      insertImageFnRef.current = null;
      insertVideoFnRef.current = null;
    };
  }, []);

  // 使用 ref 保存最新的 currentInsertType，供 closeModal 使用
  const currentInsertTypeRef = useRef(currentInsertType);
  useEffect(() => {
    currentInsertTypeRef.current = currentInsertType;
  }, [currentInsertType]);

  /**
   * 文件选择器关闭回调
   * @param result - 用户选择的文件数据，结构需根据实际 FilesSelector 返回值调整
   */
  const handleFilesSelectorClose = useCallback((result: any) => {
    setFilesSelectorVisible(false);
    if (!result) return;

    const { fileRows } = result.data;
    if (fileRows) {
      // 根据当前插入类型决定使用图片或视频插入函数
      if (currentInsertTypeRef.current === 'image' && insertImageFnRef.current) {
        for (const item of fileRows) {
          const { serverUrl, destination, fileName } = item;
          insertImageFnRef.current(`${serverUrl}/${destination}/${fileName}`);
        }
        insertImageFnRef.current = null;
      }

      if (currentInsertTypeRef.current === 'video' && insertVideoFnRef.current) {
        for (const item of fileRows) {
          const { serverUrl, destination, fileName } = item;
          const videoUrl = `${serverUrl}/${destination}/${fileName}`;
          // 视频插入函数，如需封面可传入第二个参数 posterUrl
          insertVideoFnRef.current(videoUrl);
        }
        insertVideoFnRef.current = null;
      }
    }
  }, []);

  // ========== 文件选择器配置 ==========
  const filesSelectorProps: FilesSelectorProps = useMemo(() => ({
    /** 显示 */
    visible: filesSelectorVisible,
    defaultService: defaultService!,
    /**
     * 文件选择器关闭回调
     * @param returnData - 用户选择的文件数据，结构需根据实际 FilesSelector 返回值调整
     */
    closeModal: handleFilesSelectorClose,
    titleName: '文件选择器'
  }), [filesSelectorVisible, defaultService, handleFilesSelectorClose]);

  // ========== 自定义编辑器配置（覆盖上传图片与上传视频行为） ==========
  const customEditorConfig: Partial<IEditorConfig> = useMemo(() => ({
    MENU_CONF: {
      uploadImage: {
        // 自定义图片上传：打开文件选择器，而非默认上传
        customBrowseAndUpload(insertFn: any) {
          const service = defaultServiceRef.current;
          if (!service) {
            antdMessage('error', '未配置服务基础地址：defaultService');
            return;
          }
          // 保存 insertFn 供后续使用
          insertImageFnRef.current = insertFn;
          setCurrentInsertType('image');
          // 打开文件选择器
          setFilesSelectorVisible(true);
        }
      },
      uploadVideo: {
        // 自定义视频上传：与图片一样打开文件选择器
        customBrowseAndUpload(insertFn: any) {
          const service = defaultServiceRef.current;
          if (!service) {
            antdMessage('error', '未配置服务基础地址：defaultService');
            return;
          }
          insertVideoFnRef.current = insertFn;
          setCurrentInsertType('video');
          setFilesSelectorVisible(true);
        }
      }
    }
  }), []);

  // 确保 value 不为 undefined，始终以受控模式创建编辑器
  const normalizedValue = value !== undefined ? value : '';

  // 若外部 value 初始为 undefined，手动初始化表单字段为空字符串
  const didInitRef = useRef(false);
  useEffect(() => {
    if (!didInitRef.current && (value === undefined || value === null)) {
      didInitRef.current = true;
      onChange?.('');
    }
  }, []);

  return (
    <>
      <WangeditorV5
        {...editorRestProps}
        value={normalizedValue}
        onChange={handleChange}
        editorConfig={customEditorConfig}
      />
      <FilesSelector {...filesSelectorProps} />
    </>
  );
};

export default WangeditorV5ForAntD;